import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';

const stripHtml = (content) => {
  if (!content) return '';
  // Si le contenu commence par ```json ou contient du JSON brut
  if (content.includes('```json') || content.trim().startsWith('{') || content.trim().startsWith('`')) {
    return 'Article généré - cliquez sur "Voir l\'article" pour le consulter.';
  }
  // Sinon enlever les balises HTML
  return content.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
};

function Contenus() {
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [sujets, setSujets] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingSujetId, setGeneratingSujetId] = useState('');
  const [publishingArticleId, setPublishingArticleId] = useState('');
  const [publishModalArticle, setPublishModalArticle] = useState(null);
  const [wpCategories, setWpCategories] = useState([]);
  const [blogCategories, setBlogCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedBlogCategories, setSelectedBlogCategories] = useState([]);
  const [viewArticle, setViewArticle] = useState(null);
  const [editingArticle, setEditingArticle] = useState(null);
  const [savingEdition, setSavingEdition] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [rewritingWithAi, setRewritingWithAi] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '',
    titre: '',
    seoTitle: '',
    seoDescription: '',
    contenu: '',
  });
  const [error, setError] = useState('');
  const selectedSite = useMemo(
    () => sites.find((site) => site.id === selectedSiteId) || null,
    [sites, selectedSiteId]
  );
  const isSelectedSiteSupabase = selectedSite?.type === 'supabase';

  const loadSites = async () => {
    const response = await apiFetch('/api/sites');
    if (!response.ok) throw new Error('Erreur chargement sites');
    const data = await response.json();
    const nextSites = Array.isArray(data) ? data : [];
    setSites(nextSites);
    if (nextSites.length > 0) {
      setSelectedSiteId((current) => current || nextSites[0].id);
    }
  };

  const loadPageData = async (siteId) => {
    if (!siteId) {
      setSujets([]);
      setArticles([]);
      return;
    }

    const [sujetsRes, articlesRes] = await Promise.all([
      apiFetch(`/api/sujets/${siteId}`),
      apiFetch(`/api/articles/${siteId}`),
    ]);
    if (!sujetsRes.ok || !articlesRes.ok) {
      throw new Error('Erreur chargement contenus');
    }

    const [sujetsData, articlesData] = await Promise.all([sujetsRes.json(), articlesRes.json()]);
    const sujetsList = Array.isArray(sujetsData) ? sujetsData : [];
    const articlesList = Array.isArray(articlesData) ? articlesData : [];

    setSujets(sujetsList.filter((sujet) => sujet.statut === 'validé'));
    setArticles(articlesList);
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        setLoading(true);
        setError('');
        await loadSites();
      } catch (requestError) {
        setError('Impossible de charger les sites.');
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        await loadPageData(selectedSiteId);
      } catch (requestError) {
        setError('Impossible de charger les sujets et articles.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedSiteId]);

  const handleGenerateArticle = async (sujetId) => {
    try {
      setError('');
      setGeneratingSujetId(sujetId);
      const response = await apiFetch(`/api/articles/generer/${sujetId}`, { method: 'POST' });
      if (!response.ok) {
        throw new Error('Erreur génération article');
      }
      const created = await response.json();
      setArticles((current) => [created, ...current]);
      await apiFetch(`/api/sujets/${sujetId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut: 'utilisé' }),
      });
      setSujets((current) => current.filter((sujet) => sujet.id !== sujetId));
    } catch (requestError) {
      setError("Impossible de générer l'article.");
    } finally {
      setGeneratingSujetId('');
    }
  };

  const handleDeleteSujet = async (sujetId) => {
    try {
      setError('');
      const response = await apiFetch(`/api/sujets/${sujetId}`, { method: 'DELETE' });
      if (!response.ok) {
        throw new Error('Erreur suppression sujet');
      }
      setSujets((current) => current.filter((sujet) => sujet.id !== sujetId));
    } catch (requestError) {
      setError('Impossible de supprimer ce sujet.');
    }
  };

  const openPublishModal = async (article) => {
    const articleSite = sites.find((site) => site.id === article.siteId);
    const isSupabaseSite = articleSite?.type === 'supabase';
    try {
      setError('');
      setSelectedCategoryId('');
      setNewCategoryName('');
      setSelectedBlogCategories([]);
      setLoadingCategories(true);
      if (isSupabaseSite) {
        const response = await apiFetch(`/api/articles/blog-categories/${article.siteId}`);
        if (!response.ok) {
          throw new Error('Erreur chargement categories blog');
        }
        const data = await response.json();
        setWpCategories([]);
        setBlogCategories(Array.isArray(data) ? data : []);
        setPublishModalArticle(article);
        return;
      }
      const response = await apiFetch(`/api/articles/categories/${article.siteId}`);
      if (!response.ok) {
        throw new Error('Erreur chargement categories');
      }
      const data = await response.json();
      setWpCategories(Array.isArray(data) ? data : []);
      setBlogCategories([]);
      setPublishModalArticle(article);
    } catch (requestError) {
      if (!isSupabaseSite) {
        setError('Impossible de charger les catégories WordPress.');
      } else {
        setError('Impossible de charger les catégories du blog.');
      }
      setPublishModalArticle(null);
    } finally {
      setLoadingCategories(false);
    }
  };

  const closePublishModal = () => {
    setPublishModalArticle(null);
    setWpCategories([]);
    setBlogCategories([]);
    setSelectedCategoryId('');
    setNewCategoryName('');
    setSelectedBlogCategories([]);
  };

  const handlePublishArticle = async () => {
    if (!publishModalArticle?.id) return;

    try {
      setError('');
      setPublishingArticleId(publishModalArticle.id);
      const response = await apiFetch(`/api/articles/publier/${publishModalArticle.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categoryId: selectedCategoryId ? Number(selectedCategoryId) : null,
          newCategoryName: newCategoryName.trim() || null,
          categories: selectedBlogCategories,
        }),
      });
      if (!response.ok) {
        throw new Error('Erreur publication article');
      }
      const result = await response.json();
      if (result?.article) {
        setArticles((current) =>
          current.map((article) =>
            article.id === publishModalArticle.id ? result.article : article
          )
        );
      } else {
        await loadPageData(selectedSiteId);
      }
      closePublishModal();
    } catch (requestError) {
      setError('Impossible de publier cet article.');
    } finally {
      setPublishingArticleId('');
    }
  };

  const handleViewArticle = async (articleId) => {
    try {
      setError('');
      const response = await apiFetch(`/api/articles/detail/${articleId}`);
      if (!response.ok) {
        throw new Error('Erreur chargement detail article');
      }
      const data = await response.json();
      setViewArticle(data);
    } catch (requestError) {
      setError("Impossible de charger le contenu de l'article.");
    }
  };

  const openEditModal = async (articleId) => {
    try {
      setError('');
      const response = await apiFetch(`/api/articles/detail/${articleId}`);
      if (!response.ok) {
        throw new Error('Erreur chargement article');
      }
      const data = await response.json();
      setEditingArticle(data);
      setEditForm({
        id: data.id,
        titre: data.titre || '',
        seoTitle: data.seoTitle || '',
        seoDescription: data.seoDescription || '',
        contenu: data.contenu || '',
      });
      setAiPrompt('');
    } catch {
      setError("Impossible de charger l'article pour modification.");
    }
  };

  const closeEditModal = () => {
    setEditingArticle(null);
    setSavingEdition(false);
    setEditForm({
      id: '',
      titre: '',
      seoTitle: '',
      seoDescription: '',
      contenu: '',
    });
    setAiPrompt('');
    setRewritingWithAi(false);
  };

  const saveEditedArticle = async () => {
    if (!editForm.id) return;
    try {
      setError('');
      setSavingEdition(true);
      const response = await apiFetch(`/api/articles/${editForm.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          titre: editForm.titre,
          seoTitle: editForm.seoTitle.slice(0, 60),
          seoDescription: editForm.seoDescription.slice(0, 155),
          contenu: editForm.contenu,
        }),
      });
      if (!response.ok) {
        throw new Error('Erreur sauvegarde article');
      }
      const updated = await response.json();
      setArticles((current) =>
        current.map((article) => (article.id === updated.id ? updated : article))
      );
      setEditingArticle(updated);
    } catch {
      setError("Impossible de sauvegarder les modifications de l'article.");
    } finally {
      setSavingEdition(false);
    }
  };

  const rewriteWithAi = async () => {
    if (!editForm.id || !aiPrompt.trim()) return;
    try {
      setError('');
      setRewritingWithAi(true);
      const response = await apiFetch(`/api/articles/rewrite/${editForm.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: aiPrompt.trim() }),
      });
      if (!response.ok) {
        throw new Error('Erreur réécriture IA');
      }
      const rewritten = await response.json();
      setEditForm((current) => ({
        ...current,
        seoTitle: String(rewritten?.seoTitle || current.seoTitle).slice(0, 60),
        seoDescription: String(rewritten?.seoDescription || current.seoDescription).slice(0, 155),
        contenu: String(rewritten?.contenu || current.contenu),
      }));
    } catch {
      setError("Impossible de réécrire l'article avec l'IA.");
    } finally {
      setRewritingWithAi(false);
    }
  };

  const handleDeleteArticle = async (article) => {
    if (!article?.id) return;

    if (
      article.statut === 'publié' &&
      !window.confirm('Cet article est publié sur WordPress, supprimer quand même ?')
    ) {
      return;
    }

    try {
      setError('');
      const response = await apiFetch(`/api/articles/${article.id}`, { method: 'DELETE' });
      if (!response.ok) {
        throw new Error('Erreur suppression article');
      }
      setArticles((current) => current.filter((currentArticle) => currentArticle.id !== article.id));
    } catch (requestError) {
      setError("Impossible de supprimer cet article.");
    }
  };

  const generatedOrPublished = useMemo(() => articles, [articles]);
  const toggleBlogCategory = (slug) => {
    setSelectedBlogCategories((current) =>
      current.includes(slug) ? current.filter((item) => item !== slug) : [...current, slug]
    );
  };

  return (
    <section className="contenus-page">
      <header className="contenus-header">
        <h1>Génération d'articles</h1>
        <p>
          Créez des contenus SEO complets puis publiez-les automatiquement sur{' '}
          {isSelectedSiteSupabase ? 'votre blog Supabase' : 'WordPress'}.
        </p>
      </header>

      {error && <p className="sites-error">{error}</p>}

      <div className="contenus-toolbar">
        <label htmlFor="contenusSiteSelect">Site sélectionné</label>
        <select
          id="contenusSiteSelect"
          value={selectedSiteId}
          onChange={(event) => setSelectedSiteId(event.target.value)}
          disabled={loading || sites.length === 0}
        >
          {sites.length === 0 ? (
            <option value="">Aucun site disponible</option>
          ) : (
            sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.nom}
              </option>
            ))
          )}
        </select>
      </div>

      <section className="contenus-section">
        <h2>Sujets validés</h2>
        {loading ? (
          <div className="sites-empty-card">
            <p>Chargement...</p>
          </div>
        ) : sujets.length === 0 ? (
          <div className="sites-empty-card">
            <p>Aucun sujet validé disponible pour ce site.</p>
          </div>
        ) : (
          <div className="contenus-sujets-grid">
            {sujets.map((sujet) => (
              <article key={sujet.id} className="sujet-card">
                <h3>{sujet.titre}</h3>
                <p className="sujet-keyword">Mot-clé : {sujet.motCle}</p>
                <p className="sujet-description">{sujet.description || 'Sans description.'}</p>
                <div className="article-actions">
                  <button
                    type="button"
                    className="primary-button contenus-generate-button"
                    onClick={() => handleGenerateArticle(sujet.id)}
                    disabled={generatingSujetId === sujet.id}
                  >
                    {generatingSujetId === sujet.id ? 'Génération...' : "Générer l'article"}
                  </button>
                  <button
                    type="button"
                    className="site-delete-button"
                    onClick={() => handleDeleteSujet(sujet.id)}
                  >
                    Supprimer
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="contenus-section">
        <h2>Articles générés</h2>
        {loading ? null : generatedOrPublished.length === 0 ? (
          <div className="sites-empty-card">
            <p>Aucun article généré pour ce site.</p>
          </div>
        ) : (
          <div className="contenus-articles-grid">
            {generatedOrPublished.map((article) => (
              <article key={article.id} className="article-card">
                <div className="article-card-header">
                  <h3>{article.titre}</h3>
                  <span
                    className={`article-status ${
                      article.statut === 'publié' ? 'published' : 'generated'
                    }`}
                  >
                    {article.statut}
                  </span>
                </div>
                <p className="article-excerpt">
                  {stripHtml(article.contenu).slice(0, 200)}
                  {stripHtml(article.contenu).length > 200 ? '...' : ''}
                </p>
                <div className="article-actions">
                  {article.statut === 'généré' ? (
                    <>
                      <button
                        type="button"
                        className="site-edit-button"
                        onClick={() => handleViewArticle(article.id)}
                      >
                        Prévisualiser
                      </button>
                      <button
                        type="button"
                        className="secondary-button"
                        onClick={() => openEditModal(article.id)}
                      >
                        Modifier
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="site-edit-button"
                      onClick={() => handleViewArticle(article.id)}
                    >
                      Voir l'article
                    </button>
                  )}
                  {article.statut === 'généré' && (
                    <button
                      type="button"
                      className="sujet-validate-button"
                      onClick={() => openPublishModal(article)}
                      disabled={publishingArticleId === article.id}
                    >
                      {isSelectedSiteSupabase ? 'Publier sur le blog' : 'Publier sur WordPress'}
                    </button>
                  )}
                  <button
                    type="button"
                    className="site-delete-button"
                    onClick={() => handleDeleteArticle(article)}
                  >
                    Supprimer
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {viewArticle && (
        <div className="sites-modal-overlay" role="presentation" onClick={() => setViewArticle(null)}>
          <div
            className="sites-modal contenus-article-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Détail article"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="sites-modal-header">
              <h2>{viewArticle.titre}</h2>
              <button type="button" className="sites-close-button" onClick={() => setViewArticle(null)}>
                ×
              </button>
            </header>
            <div
              className="article-html-content"
              dangerouslySetInnerHTML={{ __html: viewArticle.contenu || '' }}
            />
          </div>
        </div>
      )}

      {publishModalArticle && (
        <div className="sites-modal-overlay" role="presentation" onClick={closePublishModal}>
          <div
            className="sites-modal contenus-publish-modal"
            role="dialog"
            aria-modal="true"
            aria-label={isSelectedSiteSupabase ? 'Publier sur le blog' : 'Publier sur WordPress'}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="sites-modal-header">
              <h2>{isSelectedSiteSupabase ? 'Publier sur le blog' : 'Publier sur WordPress'}</h2>
              <button type="button" className="sites-close-button" onClick={closePublishModal}>
                ×
              </button>
            </header>
            <div className="publish-form">
              <p className="publish-target-title">{publishModalArticle.titre}</p>

              {!isSelectedSiteSupabase && (
                <>
                  <label htmlFor="wpCategory">Catégorie existante</label>
                  <select
                    id="wpCategory"
                    value={selectedCategoryId}
                    onChange={(event) => setSelectedCategoryId(event.target.value)}
                    disabled={loadingCategories}
                  >
                    <option value="">Aucune catégorie</option>
                    {wpCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>

                  <label htmlFor="newCategory">Nouvelle catégorie (optionnel)</label>
                  <input
                    id="newCategory"
                    type="text"
                    value={newCategoryName}
                    onChange={(event) => setNewCategoryName(event.target.value)}
                    placeholder="Ex: SEO Local"
                  />
                </>
              )}

              {isSelectedSiteSupabase && (
                <>
                  <label>Catégories du blog</label>
                  {loadingCategories ? (
                    <p className="dashboard-muted">Chargement des catégories...</p>
                  ) : blogCategories.length === 0 ? (
                    <p className="dashboard-muted">Aucune catégorie disponible (fallback automatique).</p>
                  ) : (
                    <div className="parametres-service-item">
                      {blogCategories.map((category) => (
                        <label key={category.slug}>
                          <input
                            type="checkbox"
                            checked={selectedBlogCategories.includes(category.slug)}
                            onChange={() => toggleBlogCategory(category.slug)}
                          />{' '}
                          {category.name || category.slug}
                        </label>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div className="sites-form-actions">
                <button type="button" className="secondary-button" onClick={closePublishModal}>
                  Annuler
                </button>
                <button
                  type="button"
                  className="primary-button"
                  onClick={handlePublishArticle}
                  disabled={publishingArticleId === publishModalArticle.id}
                >
                  {publishingArticleId === publishModalArticle.id
                    ? 'Publication...'
                    : 'Publier maintenant'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editingArticle && (
        <div className="sites-modal-overlay" role="presentation" onClick={closeEditModal}>
          <div
            className="sites-modal contenus-edit-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Modifier article"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="sites-modal-header">
              <h2>Modifier l'article</h2>
              <button type="button" className="sites-close-button" onClick={closeEditModal}>
                ×
              </button>
            </header>

            <div className="contenus-edit-layout">
              <div className="contenus-edit-form">
                <label htmlFor="editTitre">Titre</label>
                <input
                  id="editTitre"
                  type="text"
                  value={editForm.titre}
                  onChange={(event) =>
                    setEditForm((current) => ({ ...current, titre: event.target.value }))
                  }
                />

                <label htmlFor="editSeoTitle">SEO Title (max 60)</label>
                <input
                  id="editSeoTitle"
                  type="text"
                  maxLength={60}
                  value={editForm.seoTitle}
                  onChange={(event) =>
                    setEditForm((current) => ({ ...current, seoTitle: event.target.value }))
                  }
                />
                <p className="contenus-counter">{editForm.seoTitle.length}/60</p>

                <label htmlFor="editSeoDescription">SEO Description (max 155)</label>
                <textarea
                  id="editSeoDescription"
                  maxLength={155}
                  rows={4}
                  value={editForm.seoDescription}
                  onChange={(event) =>
                    setEditForm((current) => ({ ...current, seoDescription: event.target.value }))
                  }
                />
                <p className="contenus-counter">{editForm.seoDescription.length}/155</p>

                <label htmlFor="editContenu">Contenu HTML</label>
                <textarea
                  id="editContenu"
                  className="contenus-html-textarea"
                  rows={22}
                  value={editForm.contenu}
                  onChange={(event) =>
                    setEditForm((current) => ({ ...current, contenu: event.target.value }))
                  }
                />

                <div className="contenus-ai-box">
                  <h3>Assistant IA</h3>
                  <label htmlFor="aiPrompt">Prompt libre</label>
                  <textarea
                    id="aiPrompt"
                    rows={4}
                    placeholder='Ex: "Rends le ton plus dynamique"'
                    value={aiPrompt}
                    onChange={(event) => setAiPrompt(event.target.value)}
                  />
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={rewriteWithAi}
                    disabled={rewritingWithAi || !aiPrompt.trim()}
                  >
                    {rewritingWithAi ? 'Réécriture en cours...' : "Réécrire avec l'IA"}
                  </button>
                </div>
              </div>

              <div className="contenus-edit-preview">
                <h3>Prévisualisation live</h3>
                <div
                  className="article-html-content"
                  dangerouslySetInnerHTML={{ __html: editForm.contenu || '' }}
                />
              </div>
            </div>

            <div className="sites-form-actions">
              <button type="button" className="secondary-button" onClick={closeEditModal}>
                Fermer
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={saveEditedArticle}
                disabled={savingEdition}
              >
                {savingEdition ? 'Sauvegarde...' : 'Sauvegarder les modifications'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Contenus;
